import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/switch';
import PaginationFooter from '@/components/PaginationFooter';
import { PencilIcon, PlusIcon, MagnifyingGlassIcon, FunnelIcon, CalendarIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from '@tanstack/react-router';
import GooglePlacesAutocomplete from '@/components/GooglePlacesAutocomplete';
import MultipleSelector, { Option } from '@/components/ui/multiple-selector';

interface EmployeeGroupBasic {
  id: string;
  name: string;
}

interface TimesheetScheduler {
  id: string;
  organization_id: string;
  name: string;
  timezone: string;
  is_archived: boolean;
  is_connecteam_synced: boolean;
  connecteam_scheduler_id?: string | null;
  gps_address?: string | null;
  gps_latitude?: number | null;
  gps_longitude?: number | null;
  company_id: string;
  group_ids?: string[];
  employee_groups?: EmployeeGroupBasic[];
  created_at: string;
  updated_at?: string | null;
}

interface Company {
  id: string;
  name: string;
  tax_id?: string | null;
  is_active: boolean;
}

interface EmployeeGroup {
  id: string;
  name: string;
  description?: string | null;
  is_active: boolean;
}

interface ListResponse {
  schedulers: TimesheetScheduler[];
  total: number;
  page: number;
  per_page: number;
}

interface Pagination {
  page: number;
  total: number;
  pages: number;
  limit: number;
}

export default function SchedulersPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [schedulers, setSchedulers] = useState<TimesheetScheduler[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, total: 0, pages: 0, limit: 20 });
  const [search, setSearch] = useState<string>('');
  const [filterType, setFilterType] = useState<'all' | 'connecteam'>('all');
  const [includeArchived, setIncludeArchived] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Companies state
  const [companies, setCompanies] = useState<Company[]>([]);

  // Employee groups state
  const [employeeGroups, setEmployeeGroups] = useState<EmployeeGroup[]>([]);

  // Dialog + form state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingScheduler, setEditingScheduler] = useState<TimesheetScheduler | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState<{
    name: string;
    timezone: string;
    gps_address: string;
    gps_latitude: string;
    gps_longitude: string;
    company_id: string;
    group_ids: string[];
  }>({
    name: '',
    timezone: 'UTC',
    gps_address: '',
    gps_latitude: '',
    gps_longitude: '',
    company_id: '',
    group_ids: []
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Debounce search input
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(h);
  }, [search]);

  // Reset to first page on filter changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [debouncedSearch, includeArchived, filterType]);

  const pages = useMemo(() => {
    return pagination.limit > 0 ? Math.max(1, Math.ceil(pagination.total / pagination.limit)) : 1;
  }, [pagination.total, pagination.limit]);

  useEffect(() => {
    fetchCompanies();
    fetchEmployeeGroups();
  }, []);

  useEffect(() => {
    fetchSchedulers();
  }, [pagination.page, pagination.limit, debouncedSearch, includeArchived, filterType]);

  const fetchCompanies = async () => {
    try {
      const response = await apiFetch('/api/companies/?limit=100');
      if (response.ok) {
        const data = await response.json();
        setCompanies(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const fetchEmployeeGroups = async () => {
    try {
      const response = await apiFetch('/api/people/groups/?limit=100&is_active=true');
      if (response.ok) {
        const data = await response.json();
        setEmployeeGroups(data.groups || []);
      }
    } catch (err) {
      console.error('Error fetching employee groups:', err);
    }
  };

  const fetchSchedulers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(pagination.page),
        per_page: String(pagination.limit),
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (includeArchived) params.set('include_archived', 'true');
      if (filterType === 'connecteam') params.set('connecteam_only', 'true');

      const resp = await apiFetch(`/api/timesheet/schedulers?${params.toString()}`);
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`HTTP ${resp.status} ${txt}`);
      }
      const data: ListResponse = await resp.json();
      setSchedulers(data.schedulers || []);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
        pages: Math.max(1, Math.ceil((data.total || 0) / (data.per_page || prev.limit))),
        page: data.page || prev.page,
        limit: data.per_page || prev.limit,
      }));
    } catch (e: any) {
      console.error('Failed to fetch schedulers:', e);
      setError(e?.message || 'Failed to load schedulers');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      timezone: 'UTC',
      gps_address: '',
      gps_latitude: '',
      gps_longitude: '',
      company_id: companies.length > 0 ? companies[0].id : '',
      group_ids: []
    });
    setEditingScheduler(null);
  };

  const openEditDialog = (s: TimesheetScheduler) => {
    setEditingScheduler(s);
    setFormData({
      name: s.name || '',
      timezone: s.timezone || 'UTC',
      gps_address: s.gps_address || '',
      gps_latitude: s.gps_latitude?.toString() || '',
      gps_longitude: s.gps_longitude?.toString() || '',
      company_id: s.company_id || '',
      group_ids: s.group_ids || []
    });
  };

  const handleCreateScheduler = async () => {
    setFormLoading(true);
    try {
      const payload = {
        name: formData.name,
        timezone: formData.timezone,
        is_archived: false,
        gps_address: formData.gps_address || null,
        gps_latitude: formData.gps_latitude ? parseFloat(formData.gps_latitude) : null,
        gps_longitude: formData.gps_longitude ? parseFloat(formData.gps_longitude) : null,
        company_id: formData.company_id,
        group_ids: formData.group_ids.length > 0 ? formData.group_ids : null
      };
      const resp = await apiFetch('/api/timesheet/schedulers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to create scheduler');
      }
      setShowCreateDialog(false);
      resetForm();
      fetchSchedulers();
    } catch (e: any) {
      console.error('Failed to create scheduler:', e);
      setError(e?.message || 'Failed to create scheduler');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateScheduler = async () => {
    if (!editingScheduler) return;
    setFormLoading(true);
    try {
      // For Connecteam-synced schedulers, only send company_id, GPS fields, and group_ids
      const payload = editingScheduler.is_connecteam_synced
        ? {
            company_id: formData.company_id,
            gps_address: formData.gps_address || null,
            gps_latitude: formData.gps_latitude ? parseFloat(formData.gps_latitude) : null,
            gps_longitude: formData.gps_longitude ? parseFloat(formData.gps_longitude) : null,
            group_ids: formData.group_ids
          }
        : {
            name: formData.name,
            timezone: formData.timezone,
            gps_address: formData.gps_address || null,
            gps_latitude: formData.gps_latitude ? parseFloat(formData.gps_latitude) : null,
            gps_longitude: formData.gps_longitude ? parseFloat(formData.gps_longitude) : null,
            company_id: formData.company_id,
            group_ids: formData.group_ids
          };

      const resp = await apiFetch(`/api/timesheet/schedulers/${editingScheduler.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to update scheduler');
      }
      setEditingScheduler(null);
      resetForm();
      fetchSchedulers();
    } catch (e: any) {
      console.error('Failed to update scheduler:', e);
      setError(e?.message || 'Failed to update scheduler');
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <CalendarIcon className="h-8 w-8" />
            {t('employees.jobs.schedulers', 'common')}
            </h1>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate({ to: '/people/schedulers/generate-schedule' })}
            >
              <SparklesIcon className="h-4 w-4 mr-2" />
              Generate Schedule
            </Button>
            <Button onClick={() => setShowCreateDialog(true)}>
              <PlusIcon className="h-4 w-4 mr-2" />
              {t('actions.addNew', 'common')} {t('employees.jobs.schedulers', 'common')}
            </Button>
          </div>
        </div>

        {/* Create Scheduler Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('actions.addNew', 'common')} {t('employees.jobs.schedulers', 'common')}</DialogTitle>
              <DialogDescription>
                {t('employees.jobs.subtitle', 'common')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">{t('forms.name', 'common')} *</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Downtown" />
              </div>
              <div>
                <Label htmlFor="company_id">Company *</Label>
                <Select
                  value={formData.company_id}
                  onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="timezone">Timezone</Label>
                <Input id="timezone" value={formData.timezone} onChange={(e) => setFormData({ ...formData, timezone: e.target.value })} placeholder="UTC" />
              </div>
              <div>
                <Label htmlFor="gps_address">Address</Label>
                <GooglePlacesAutocomplete
                  value={formData.gps_address}
                  onAddressChange={(val: string) => setFormData(prev => ({ ...prev, gps_address: val }))}
                  onPlaceSelected={(addr: string, lat: number | null, lng: number | null) => {
                    setFormData(prev => ({
                      ...prev,
                      gps_address: addr,
                      gps_latitude: lat !== null ? String(lat) : '',
                      gps_longitude: lng !== null ? String(lng) : '',
                    }))
                  }}
                />
              </div>
              <div>
                <Label>Employee Groups</Label>
                <MultipleSelector
                  value={formData.group_ids.map(id => {
                    const group = employeeGroups.find(g => g.id === id);
                    return { value: id, label: group?.name || id };
                  })}
                  onChange={(options: Option[]) => {
                    setFormData(prev => ({ ...prev, group_ids: options.map(o => o.value) }));
                  }}
                  options={employeeGroups.map(g => ({ value: g.id, label: g.name }))}
                  placeholder="Select employee groups..."
                  emptyIndicator={
                    <p className="text-center text-sm leading-10 text-muted-foreground">
                      No groups found.
                    </p>
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t('actions.cancel', 'common')}</Button>
              <Button onClick={handleCreateScheduler} disabled={formLoading || !formData.name || !formData.company_id}>
                {formLoading ? t('actions.saving', 'common') : t('actions.create', 'common')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Scheduler Dialog */}
        <Dialog open={!!editingScheduler} onOpenChange={() => setEditingScheduler(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('actions.edit', 'common')} {t('employees.jobs.schedulers', 'common')}</DialogTitle>
              <DialogDescription>
                {t('employees.jobs.subtitle', 'common')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {editingScheduler?.is_connecteam_synced && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
                  This scheduler is synced from Connecteam. Only company assignment, GPS location, and employee groups can be changed.
                </div>
              )}
              <div>
                <Label htmlFor="edit-name">{t('forms.name', 'common')} *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Downtown"
                  disabled={editingScheduler?.is_connecteam_synced}
                />
              </div>
              <div>
                <Label htmlFor="edit-company_id">Company *</Label>
                <Select
                  value={formData.company_id}
                  onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-timezone">Timezone</Label>
                <Input
                  id="edit-timezone"
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  placeholder="UTC"
                  disabled={editingScheduler?.is_connecteam_synced}
                />
              </div>
              <div>
                <Label htmlFor="edit-gps_address">Address</Label>
                <GooglePlacesAutocomplete
                  value={formData.gps_address}
                  onAddressChange={(val: string) => setFormData(prev => ({ ...prev, gps_address: val }))}
                  onPlaceSelected={(addr: string, lat: number | null, lng: number | null) => {
                    setFormData(prev => ({
                      ...prev,
                      gps_address: addr,
                      gps_latitude: lat !== null ? String(lat) : '',
                      gps_longitude: lng !== null ? String(lng) : '',
                    }))
                  }}
                />
              </div>
              <div>
                <Label>Employee Groups</Label>
                <MultipleSelector
                  value={formData.group_ids.map(id => {
                    const group = employeeGroups.find(g => g.id === id);
                    return { value: id, label: group?.name || id };
                  })}
                  onChange={(options: Option[]) => {
                    setFormData(prev => ({ ...prev, group_ids: options.map(o => o.value) }));
                  }}
                  options={employeeGroups.map(g => ({ value: g.id, label: g.name }))}
                  placeholder="Select employee groups..."
                  emptyIndicator={
                    <p className="text-center text-sm leading-10 text-muted-foreground">
                      No groups found.
                    </p>
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingScheduler(null)}>{t('actions.cancel', 'common')}</Button>
              <Button onClick={handleUpdateScheduler} disabled={formLoading || !formData.name || !formData.company_id}>
                {formLoading ? t('actions.saving', 'common') : t('actions.update', 'common')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Filters */}
        <div className="flex gap-4 items-center flex-wrap">
          <div className="relative flex-1 min-w-64">
            <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('actions.search', 'common')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterType} onValueChange={(value: any) => setFilterType(value)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('actions.all', 'common')}</SelectItem>
              <SelectItem value="connecteam">{t('employees.jobs.connecteamOnly', 'common')}</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
            <FunnelIcon className="h-4 w-4 mr-2" />
            {t('actions.advancedFilters', 'common')}
          </Button>
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-2">
                <Switch checked={includeArchived} onChange={setIncludeArchived} />
                <Label>{t('status.deleted', 'common')}</Label>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error State */}
        {error && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Table (no card wrapper) */}
        <div>
          <Table className="w-full border">
            <TableHeader>
              <TableRow>
                <TableHead>{t('forms.name', 'common')}</TableHead>
                <TableHead>{t('forms.type', 'common')}</TableHead>
                <TableHead>Timezone</TableHead>
                <TableHead>{t('status.active', 'common')}</TableHead>
                <TableHead>{t('actions.actions', 'common')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: pagination.limit }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-16 rounded-full" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-12" /></TableCell>
                  </TableRow>
                ))
              ) : schedulers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    {t('messages.noResults', 'common')}
                  </TableCell>
                </TableRow>
              ) : (
                schedulers.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">
                      <Link to={`/people/schedulers/${s.id}` as any} className="hover:underline">
                        {s.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {s.is_connecteam_synced ? (
                        <Badge variant="default">Connecteam</Badge>
                      ) : (
                        <Badge variant="secondary">Manual</Badge>
                      )}
                    </TableCell>
                    <TableCell>{s.timezone || 'UTC'}</TableCell>
                    <TableCell>
                      <Badge variant={s.is_archived ? 'secondary' : 'default'}>
                        {s.is_archived ? t('status.inactive', 'common') : t('status.active', 'common')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(s)}>
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <PaginationFooter
          pagination={{ ...pagination, pages }}
          onPageChange={(p) => setPagination(prev => ({ ...prev, page: p }))}
          onPageSizeChange={(size) => setPagination(prev => ({ ...prev, limit: size, page: 1 }))}
        />
      </div>
    </Layout>
  );
}

export const Route = createFileRoute('/people/schedulers/')({
  component: SchedulersPage,
})
